import React, { useState, useEffect } from "react";
import "./Divisions.css";
import api from "@api";
import { NavLink } from "react-router-dom";

function Divisions({
  seasonId,
  divisions = [],
  setDivisions,
  onDropTeam,
  onRemoveTeam,
  onReorderTeams,
  onReorderDivisions,
  divisionFilterDropdown,
  onUnassignTeamsFromDeletedDivision,
}) {
  const [dragDivIndex, setDragDivIndex] = useState(null);
  const [newDivisionName, setNewDivisionName] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingDivisionId, setEditingDivisionId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMediaDivisionId, setEditingMediaDivisionId] = useState(null);
  const [mediaForm, setMediaForm] = useState({ video_url: "", image_url: "" });
  const [editedName, setEditedName] = useState("");
  const [hasGamesByDivision, setHasGamesByDivision] = useState({});
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  useEffect(() => {
    if (editingMediaDivisionId) {
      const division = divisions.find((d) => d.id === editingMediaDivisionId);

      if (division) {
        setMediaForm({
          video_url: division.video_url || "",
          image_url: division.image_url || "",
        });
      }
    }
  }, [editingMediaDivisionId, divisions]);

  useEffect(() => {
    async function checkGames() {
      const result = {};

      await Promise.all(
        divisions.map(async (division) => {
          const res = await api.get(
            `/api/seasonsv2/${seasonId}/divisions/${division.id}/games`
          );

          result[division.id] = res.data.length > 0;
        })
      );

      setHasGamesByDivision(result);
    }

    if (divisions.length > 0) checkGames();
  }, [divisions, seasonId]);

  const handleCreateDivision = async () => {
    if (!newDivisionName.trim()) return;

    try {
      const res = await api.post(
        `/api/seasonsv2/${seasonId}/divisions`,
        { name: newDivisionName },
        { withCredentials: true }
      );

      setIsCreateModalOpen(false);
      setNewDivisionName("");
      setDivisions((prev) => [...prev, { ...res.data, teams: [] }]);
    } catch (err) {
      console.error("Error creating division:", err);
    }
  };

  const handleDeleteDivision = async (divisionId) => {
    if (!window.confirm("Delete this tournament?")) return;

    try {
      await api.delete(`/api/divisions/${divisionId}`, {
        withCredentials: true,
      });

      setDivisions((prev) => {
        const deletedDivision = prev.find((division) => division.id === divisionId);

        if (deletedDivision?.teams?.length) {
          onUnassignTeamsFromDeletedDivision?.(deletedDivision.teams);
        }

        return prev.filter((division) => division.id !== divisionId);
      });
    } catch (err) {
      console.error("Error deleting division:", err);
    }
  };



  const handleDeleteSelectedDivisions = async () => {
    if (!selectedDivisions.length) {
      return alert("Select at least one tournament.");
    }

    if (!window.confirm("Delete selected tournaments?")) return;

    try {
      const selectedDivisionObjects = divisions.filter((division) =>
        selectedDivisions.includes(division.id)
      );

      await Promise.all(
        selectedDivisions.map((divisionId) =>
          api.delete(`/api/divisions/${divisionId}`, {
            withCredentials: true,
          })
        )
      );

      selectedDivisionObjects.forEach((division) => {
        if (division?.teams?.length) {
          onUnassignTeamsFromDeletedDivision?.(division.teams);
        }
      });

      setDivisions((prev) =>
        prev.filter((division) => !selectedDivisions.includes(division.id))
      );

      setSelectedDivisions([]);
    } catch (err) {
      console.error("Error deleting selected tournaments:", err);
    }
  };

  const handleTournamentCheckboxChange = (divisionId) => {
    setSelectedDivisions((prev) =>
      prev.includes(divisionId)
        ? prev.filter((id) => id !== divisionId)
        : [...prev, divisionId]
    );
  };

  const handleSelectAllTournaments = () => {
    if (selectedDivisions.length === divisions.length) {
      setSelectedDivisions([]);
    } else {
      setSelectedDivisions(divisions.map((division) => division.id));
    }
  };

  const handleDivDragStart = (index) => {
    setDragDivIndex(index);
  };

  const handleDivDrop = (dropIndex) => {
    if (dragDivIndex === null || dragDivIndex === dropIndex) return;

    const reordered = [...divisions];
    const [moved] = reordered.splice(dragDivIndex, 1);
    reordered.splice(dropIndex, 0, moved);

    const payload = reordered.map((division, index) => ({
      id: division.id,
      position: index,
    }));

    onReorderDivisions?.(payload);
    setDragDivIndex(null);
  };

  const startEditing = (id, name) => {
    setEditingDivisionId(id);
    setEditedName(name);
    setIsEditModalOpen(true);
  };

  const cancelEdit = () => {
    setEditingDivisionId(null);
    setEditedName("");
    setIsEditModalOpen(false);
  };

  const handleRenameDivision = async (divisionId) => {
    if (!editedName.trim()) return;

    try {
      await api.put(
        `/api/divisions/${divisionId}/name`,
        { name: editedName },
        { withCredentials: true }
      );

      setDivisions((prev) =>
        prev.map((division) =>
          division.id === divisionId
            ? { ...division, name: editedName }
            : division
        )
      );

      cancelEdit();
    } catch (err) {
      console.error("Failed to rename division:", err);
    }
  };

  const handleUpdateDivisionMedia = async (divisionId) => {
    try {
      await api.put(
        `/api/seasonsv2/${seasonId}/divisions/${divisionId}/media`,
        mediaForm,
        { withCredentials: true }
      );

      setDivisions((prev) =>
        prev.map((division) =>
          division.id === divisionId
            ? {
              ...division,
              video_url: mediaForm.video_url,
              image_url: mediaForm.image_url,
            }
            : division
        )
      );

      setEditingMediaDivisionId(null);
      setMediaForm({ video_url: "", image_url: "" });
    } catch (err) {
      console.error("Failed to update division media:", err);
    }
  };

  return (
    <div className="tournamentDivisionsAuthPage-container">
      <div className="tournamentDivisionsAuthPage-card">
        <div className="tournamentDivisionsAuthPage-header">
          <div>
            <h2 className="tournamentDivisionsAuthPage-title">Tournaments</h2>

            <p className="tournamentDivisionsAuthPage-subtitle">
              Create tournaments, manage brackets, and open tournament games.
            </p>
          </div>

          <div className="tournamentDivisionsAuthPage-actions">
            {divisionFilterDropdown}

            <button
              type="button"
              className="tournamentDivisionsAuthPage-primaryBtn"
              onClick={() => setIsCreateModalOpen(true)}
            >
              + Create Tournament
            </button>
            <button
              type="button"
              className="tournamentDivisionsAuthPage-dangerBtn"
              onClick={handleDeleteSelectedDivisions}
              disabled={selectedDivisions.length === 0}
              title={
                selectedDivisions.length === 0
                  ? "Select tournaments first"
                  : "Delete selected tournaments"
              }
            >
              Delete Selected
              {selectedDivisions.length > 0 && ` (${selectedDivisions.length})`}
            </button>
          </div>
        </div>

        <div className="tournamentDivisionsAuthPage-tableWrap">
          <table className="tournamentDivisionsAuthPage-table">
            <thead>
              <tr>
                
                <th className="tournamentDivisionsAuthPage-checkboxCol">
                  <input
                    type="checkbox"
                    checked={
                      divisions.length > 0 &&
                      selectedDivisions.length === divisions.length
                    }
                    onChange={handleSelectAllTournaments}
                  />
                </th>
                <th>#</th>
                <th>Name</th>
                <th>Teams</th>
                <th>Bracket</th>
                <th>Games</th>
                <th className="tournamentDivisionsAuthPage-actionCol"></th>
              </tr>
            </thead>

            <tbody>
              {divisions.length === 0 ? (
                <tr>
                  
                  <td
                    colSpan="7"
                    className="tournamentDivisionsAuthPage-emptyCell"
                  >
                    No tournaments created yet.
                  </td>
                </tr>
              ) : (
                divisions.map((division, index) => (
                  <tr
                    key={division.id}
                    draggable
                    onDragStart={() => handleDivDragStart(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDivDrop(index);

                      const json = e.dataTransfer.getData("application/json");
                      if (!json) return;

                      const draggedData = JSON.parse(json);

                      if (!draggedData.fromIndex) {
                        onDropTeam(division.id, draggedData);
                      }
                    }}
                    onDragEnd={() => setDragDivIndex(null)}
                  >
                    <td className="tournamentDivisionsAuthPage-checkboxCol">
                      <input
                        type="checkbox"
                        checked={selectedDivisions.includes(division.id)}
                        onChange={() => handleTournamentCheckboxChange(division.id)}
                      />
                    </td>
                    <td>{index + 1}</td>

                    <td>
                      <NavLink
                        to={`/dashboard/seasons/${seasonId}/divisions/${division.id}`}
                        className="tournamentDivisionsAuthPage-nameLink"
                      >
                        {division.name}
                      </NavLink>
                    </td>

                    <td>{division.teams?.length || 0}</td>

                    <td>
                      <span
                        className={
                          division.bracket_json
                            ? "tournamentDivisionsAuthPage-statusPill tournamentDivisionsAuthPage-statusActive"
                            : "tournamentDivisionsAuthPage-statusPill tournamentDivisionsAuthPage-statusOff"
                        }
                      >
                        {division.bracket_json ? "Created" : "Not Started"}
                      </span>
                    </td>

                    <td>
                      <span
                        className={
                          hasGamesByDivision[division.id]
                            ? "tournamentDivisionsAuthPage-statusPill tournamentDivisionsAuthPage-statusActive"
                            : "tournamentDivisionsAuthPage-statusPill tournamentDivisionsAuthPage-statusOff"
                        }
                      >
                        {hasGamesByDivision[division.id] ? "Created" : "None"}
                      </span>
                    </td>

                    <td className="tournamentDivisionsAuthPage-actionCol">
                      {editingDivisionId !== division.id && (
                        <div className="tournamentDivisionsAuthPage-rowActions">

                          <button
                            type="button"
                            className="tournamentDivisionsAuthPage-pencilBtn"
                            onClick={() =>
                              startEditing(division.id, division.name)
                            }
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {isCreateModalOpen && (
        <div className="tournamentDivisionsAuthPage-modalOverlay">
          <div className="tournamentDivisionsAuthPage-modal">
            <div className="tournamentDivisionsAuthPage-modalHeader">
              <div>
                <h3>Create Tournament</h3>
                <p>Add a new tournament bracket section for this season.</p>
              </div>

              <button
                type="button"
                className="tournamentDivisionsAuthPage-closeBtn"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setNewDivisionName("");
                }}
              >
                ✕
              </button>
            </div>

            <div className="tournamentDivisionsAuthPage-form">
              <label>
                Tournament Name
                <input
                  type="text"
                  value={newDivisionName}
                  onChange={(e) => setNewDivisionName(e.target.value)}
                  placeholder="Example: 40+ Tournament"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateDivision();
                    }

                    if (e.key === "Escape") {
                      setIsCreateModalOpen(false);
                      setNewDivisionName("");
                    }
                  }}
                />
              </label>

              <div className="tournamentDivisionsAuthPage-modalActions">
                <button
                  type="button"
                  className="tournamentDivisionsAuthPage-secondaryBtn"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setNewDivisionName("");
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="tournamentDivisionsAuthPage-primaryBtn"
                  onClick={handleCreateDivision}
                >
                  Create Tournament
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="tournamentDivisionsAuthPage-modalOverlay">
          <div className="tournamentDivisionsAuthPage-modal">
            <div className="tournamentDivisionsAuthPage-modalHeader">
              <div>
                <h3>Edit Tournament</h3>
                <p>Update the tournament name.</p>
              </div>

              <button
                type="button"
                className="tournamentDivisionsAuthPage-closeBtn"
                onClick={cancelEdit}
              >
                ✕
              </button>
            </div>

            <div className="tournamentDivisionsAuthPage-form">
              <label>
                Tournament Name
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Tournament name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editingDivisionId) {
                      handleRenameDivision(editingDivisionId);
                    }

                    if (e.key === "Escape") {
                      cancelEdit();
                    }
                  }}
                />
              </label>

              <div className="tournamentDivisionsAuthPage-modalActions">
                <button
                  type="button"
                  className="tournamentDivisionsAuthPage-secondaryBtn"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="tournamentDivisionsAuthPage-primaryBtn"
                  onClick={() => handleRenameDivision(editingDivisionId)}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default Divisions;